import { z } from 'zod';
import { CURRENCIES, DEAL_OUTCOME_REASONS, DEAL_STAGES, DEAL_STATES, DEAL_TYPES, OFFICES, } from '../enums.js';
import { optionalText, patchSchemaOf } from './common.js';
/**
 * A deal, shaped by MEFOSA's own CRM workbook.
 *
 * Replaces the earlier Initiative and its level 1-4 ladder; see
 * docs/DECISIONS.md section 6, revised 2026-08-31.
 */
export const dealInputSchema = z.object({
    /** Their own "Quota/invoice#" — LS162026, 658, "Invoice #2025-xxx". */
    quoteNumber: optionalText,
    description: optionalText,
    companyId: z.string().uuid(),
    contactId: z.string().uuid().optional(),
    ownerId: z.string().uuid().optional(),
    office: z.enum(OFFICES).default('beirut'),
    type: z.enum(DEAL_TYPES).default('service'),
    stage: z.union(DEAL_STAGES.map((s) => z.literal(s))).default(1),
    /**
     * Nullable, not merely optional. An edit has to be able to REMOVE a state,
     * and `undefined` cannot express that: JSON.stringify drops an undefined
     * key, so the field never reaches the server and the old value stands.
     */
    state: z.enum(DEAL_STATES).nullable().optional(),
    dealDate: z.string().date(),
    /**
     * Nullable for the same reason, and it matters more here: the amount and
     * `projectValueNote` are alternatives — their sheet often holds
     * "> 10,000 $" instead of a figure — so replacing one with the other must
     * clear the one being replaced, or the deal shows both.
     */
    projectValueMinor: z.number().int().nonnegative().nullable().optional(),
    currency: z.enum(CURRENCIES).default('USD'),
    /** Their sheet sometimes says "> 10,000 $" instead of a number. */
    projectValueNote: optionalText,
    /**
     * An override of the stage's default, never a copy of it. Null goes back to
     * the default, which is why this is nullable and not merely optional.
     */
    probabilityPercent: z.number().int().min(0).max(100).nullable().optional(),
    /**
     * Which reasons fit depends on the outcome the request leaves the deal in,
     * which only the server can see once the patch is applied:
     * `resolveDealOutcome()` in server/src/services/dealPatch.ts.
     */
    outcomeReason: z.enum(DEAL_OUTCOME_REASONS).nullable().optional(),
    outcomeNote: optionalText,
    notes: optionalText,
});
export const dealUpdateSchema = patchSchemaOf(dealInputSchema);
/**
 * Closing a deal. Revenue and cost are what the closed sheet records; profit
 * is DERIVED from them and never stored, so it cannot drift.
 */
export const closeDealSchema = z.object({
    stage: z.union([z.literal(4), z.literal(5)]),
    revenueMinor: z.number().int().nonnegative(),
    /**
     * Nullable, and NOT defaulted to zero. The close dialog tells people to
     * leave it blank if they do not track cost on a job; defaulting it to 0
     * turned "we did not record this" into "this job cost nothing", which is a
     * 100% margin on the profit report and no way to tell the two apart.
     */
    costMinor: z.number().int().nonnegative().nullable().optional(),
    paid: z.boolean().default(false),
    datePaid: z.string().date().optional(),
    notes: optionalText,
    /** Optional on purpose: a reason must never stand between MEFOSA and recording money. */
    outcomeReason: z.enum(DEAL_OUTCOME_REASONS).nullable().optional(),
    outcomeNote: optionalText,
});
/** Profit is revenue minus cost. Always computed, never read from a column. */
export function dealProfitMinor(deal) {
    if (deal.revenueMinor == null)
        return null;
    return deal.revenueMinor - (deal.costMinor ?? 0);
}
/**
 * Margin as a percentage of revenue, or null when there is no revenue to
 * divide by — a zero-revenue deal has no meaningful margin, and returning 0
 * would read as "broke even" rather than "not applicable".
 */
export function dealMarginPercent(deal) {
    const profit = dealProfitMinor(deal);
    if (profit == null || !deal.revenueMinor)
        return null;
    return Math.round((profit / deal.revenueMinor) * 1000) / 10;
}
//# sourceMappingURL=deal.js.map